import { type FC, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { FlowController } from "@/shared/components/FlowController";
import { sentryUtils } from "@/shared/utils/sentry";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { supabase } from "@/shared/lib/supabase";
import { stripPhone, formatPhoneNumber } from "@/shared/utils/formatters";
import { acceptInvitation } from "@/shared/utils/invitationAcceptance";

import type { FlowAnswers } from "@/shared/constants/flowTypes";

import { createInviteAcceptanceScreens } from "./inviteAcceptanceScreens";
import { useOAuthInviteReturn } from "./useOAuthInviteReturn";

interface AcceptInvitationFlowProps {
  inviterName: string;
  decedentName: string;
  token: string;
}

export const AcceptInvitationFlow: FC<AcceptInvitationFlowProps> = ({
  inviterName,
  decedentName,
  token,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const oauthProcessing = useOAuthInviteReturn();

  const { data: invitationData, isLoading: isLoadingInvitation } = useQuery({
    queryKey: ["invitation-data", token],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estate_invitations")
        .select("email, estate_id")
        .eq("invitation_token", token)
        .single();

      if (error || !data) return null;

      let contactName: string | null = null;
      let contactPhone: string | null = null;
      let contactRelationship: string | null = null;

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session && data.estate_id && data.email) {
        const { data: contact } = await supabase
          .from("estate_contacts")
          .select("first_name, last_name, phone, relationship")
          .eq("estate_id", data.estate_id)
          .eq("email", data.email.toLowerCase())
          .maybeSingle();

        if (contact) {
          contactName = [contact.first_name, contact.last_name].filter(Boolean).join(" ") || null;
          contactPhone = contact.phone ? formatPhoneNumber(contact.phone) : null;
          contactRelationship = contact.relationship ?? null;
        }
      }

      return {
        email: data.email as string,
        contactName,
        contactPhone,
        contactRelationship,
      };
    },
    enabled: !!token,
  });

  const invitationEmail = invitationData?.email ?? null;

  const { data: emailExists, isLoading: isCheckingEmail } = useQuery({
    queryKey: ["check-email-exists", invitationEmail],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("communications/email/exists", {
        body: { email: invitationEmail!.toLowerCase() },
      });

      if (error) {
        const err = new Error("Failed to check email existence");
        sentryUtils.captureError(err, "Email existence check failed during invitation acceptance", {
          email: invitationEmail,
        });
        throw err;
      }
      return data?.exists === true;
    },
    enabled: !!invitationEmail,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  const handleComplete = useCallback(() => {
    navigate(POST_LOSS_ROUTES.OVERVIEW, { replace: true });
  }, [navigate]);

  const handleStepComplete = useCallback(
    async (
      screenId: string,
      _questionId: string | undefined,
      _answer: string | string[] | undefined,
      allAnswers: FlowAnswers,
    ) => {
      if (screenId !== "create-account") return;

      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      if (!authUser) return;

      const { data: userData } = await supabase
        .from("users")
        .select("user_id, phone_number, first_name")
        .eq("auth_id", authUser.id)
        .single();
      if (!userData) return;

      const inviteeName = (allAnswers["invitee-full-name"] as string) || "";
      const inviteePhone = (allAnswers["invitee-phone"] as string) || "";
      const relationship = (allAnswers["invitee-relationship"] as string) || "";

      if (inviteeName || inviteePhone) {
        const nameParts = inviteeName.trim().split(/\s+/);
        await supabase
          .from("users")
          .update({
            ...(inviteeName
              ? {
                  first_name: nameParts[0] || userData.first_name,
                  last_name: nameParts.slice(1).join(" "),
                  full_name: inviteeName,
                }
              : {}),
            ...(inviteePhone ? { phone_number: stripPhone(inviteePhone) } : {}),
          })
          .eq("user_id", userData.user_id);
      }

      const { data: existingLink } = await supabase
        .from("user_estates")
        .select("estate_id")
        .eq("user_id", userData.user_id)
        .limit(1)
        .maybeSingle();

      if (!existingLink) {
        await acceptInvitation(token, authUser.email ?? "", relationship);
        await queryClient.invalidateQueries({ queryKey: ["app-user-data"], exact: false });
      }
    },
    [token, queryClient],
  );

  // Lock to the first definitive result so post-signup query refetches
  // don't flip the value and reset the FlowController.
  const isExistingUserRef = useRef<boolean | null>(null);
  if (isExistingUserRef.current === null && typeof emailExists === "boolean") {
    isExistingUserRef.current = emailExists;
  }
  const isExistingUser = isExistingUserRef.current ?? false;

  const isInitialLoading =
    oauthProcessing || isLoadingInvitation || (!!invitationEmail && isCheckingEmail);

  // Keep FlowController mounted after first render to prevent auth-triggered
  // query refetches from resetting step progress.
  const flowStartedRef = useRef(false);
  if (!isInitialLoading && !flowStartedRef.current) {
    flowStartedRef.current = true;
  }
  const isLoading = isInitialLoading && !flowStartedRef.current;

  const section = useMemo(
    () => ({
      ...createInviteAcceptanceScreens(inviterName, decedentName, token, {
        isExistingUser,
        invitationEmail: invitationEmail ?? null,
      }),
      onComplete: handleComplete,
      onStepComplete: handleStepComplete,
    }),
    [
      inviterName,
      decedentName,
      token,
      isExistingUser,
      invitationEmail,
      handleComplete,
      handleStepComplete,
    ],
  );

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <FlowController
      section={section}
      initialAnswers={{
        _invite_token: token,
        ...(invitationEmail ? { _invitation_email: invitationEmail } : {}),
        ...(invitationData?.contactName ? { "invitee-full-name": invitationData.contactName } : {}),
        ...(invitationData?.contactPhone ? { "invitee-phone": invitationData.contactPhone } : {}),
        ...(invitationData?.contactRelationship
          ? { "invitee-relationship": invitationData.contactRelationship }
          : {}),
      }}
    />
  );
};
